import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users as UsersIcon, Key, Eye, EyeOff, Plus, Building2 } from "lucide-react";

export default function ManageUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [busy, setBusy] = useState(false);
  const [resetId, setResetId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formName, setFormName] = useState("");
  const [formRole, setFormRole] = useState("property_admin");
  const [formProperty, setFormProperty] = useState("");

  const isSuperAdmin = me?.role === "super_admin" || me?.role === "admin";
  const canManage = isSuperAdmin || me?.role === "property_admin";

  const load = async () => {
    try {
      const [uRes, pRes] = await Promise.all([
        api.get("/users"),
        api.get("/properties"),
      ]);
      setUsers(uRes.data);
      setProperties(pRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); }, []);

  // Build property lookup: property_id -> property name
  const propMap = {};
  properties.forEach((p) => { propMap[p.id] = p.name; });

  const handleReset = async (userId, userName) => {
    if (!newPassword.trim() || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const u = users.find((x) => x.id === userId);
      if (!u) throw new Error("User not found");
      await api.put(`/users/${userId}`, {
        email: u.email,
        password: newPassword,
        name: u.name,
        role: u.role,
      });
      toast.success(`Password reset for ${userName}`);
      setResetId(null);
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!formEmail.trim() || !formPassword.trim() || !formName.trim()) return;

    const payload = {
      email: formEmail.trim().toLowerCase(),
      password: formPassword,
      name: formName.trim(),
      role: formRole,
    };

    // Super admin must pick a property; property_admin auto-uses their own
    if (isSuperAdmin) {
      if (!formProperty) {
        toast.error("Please select a property");
        return;
      }
      payload.property_id = formProperty;
    } else {
      payload.property_id = me?.property_id;
    }

    setBusy(true);
    try {
      await api.post("/users", payload);
      toast.success(`User ${formName.trim()} created`);
      setShowForm(false);
      setFormEmail("");
      setFormPassword("");
      setFormName("");
      setFormRole("property_admin");
      setFormProperty("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!canManage) {
    return (
      <div className="p-6 sm:p-10">
        <p className="text-zinc-500">You don't have access to this page.</p>
      </div>
    );
  }

  // For property_admin — find their property name
  const myPropertyName = me?.property_id ? propMap[me.property_id] : null;

  return (
    <div className="p-6 sm:p-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow mb-1">System</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">
            Users
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isSuperAdmin
              ? "Create users, manage roles, and reset passwords across all properties."
              : `Manage users for ${myPropertyName || "your property"}.`}
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="signage-btn-primary h-10 px-4 flex items-center gap-2 text-sm"
        >
          <Plus className="h-4 w-4" />
          Add User
        </button>
      </div>

      {/* Create User Form */}
      {showForm && (
        <form onSubmit={createUser} className="signage-card p-5 mb-6 max-w-lg">
          <h3 className="font-display font-bold text-lg mb-4">New User</h3>
          <div className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="mt-1 rounded-none h-10"
                placeholder="e.g. Admin Hotel"
                required
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                className="mt-1 rounded-none h-10"
                placeholder="e.g. admin@digisign.web.id"
                required
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={formPassword}
                onChange={(e) => setFormPassword(e.target.value)}
                className="mt-1 rounded-none h-10"
                placeholder="Min 6 characters"
                required
              />
            </div>
            <div>
              <Label>Role</Label>
              <select
                value={formRole}
                onChange={(e) => setFormRole(e.target.value)}
                className="mt-1 rounded-none h-10 w-full border border-zinc-300 px-3 text-sm bg-white"
              >
                <option value="property_admin">Admin</option>
                <option value="operator">Operator</option>
              </select>
            </div>

            {/* Property selector — only for super_admin */}
            {isSuperAdmin && (
              <div>
                <Label>Property</Label>
                <select
                  value={formProperty}
                  onChange={(e) => setFormProperty(e.target.value)}
                  className="mt-1 rounded-none h-10 w-full border border-zinc-300 px-3 text-sm bg-white"
                  required
                >
                  <option value="">-- Select Property --</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} {p.address ? `(${p.address})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!isSuperAdmin && myPropertyName && (
              <div className="text-xs text-zinc-500 bg-zinc-50 px-3 py-2 rounded border">
                User will be added to: <strong>{myPropertyName}</strong>
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={busy}
                className="signage-btn-primary h-9 px-4 text-sm"
              >
                {busy ? "Creating…" : "Create User"}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="signage-btn-ghost h-9 px-4 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Users List */}
      <div className="space-y-3">
        {users.length === 0 && (
          <p className="text-zinc-500 text-sm">No users yet.</p>
        )}
        {users.map((u) => {
          const isResetting = resetId === u.id;
          const propName = u.property_id ? propMap[u.property_id] : null;
          return (
            <div
              key={u.id}
              className="signage-card p-5 flex items-center justify-between"
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="h-10 w-10 bg-[#002FA7]/10 flex items-center justify-center rounded shrink-0">
                  <UsersIcon className="h-5 w-5 text-[#002FA7]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{u.name}</p>
                  <p className="text-xs text-zinc-500 truncate">{u.email}</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">
                      {u.role === "super_admin" || u.role === "admin"
                        ? "Super Admin"
                        : u.role === "property_admin"
                        ? "Admin"
                        : u.role === "operator"
                        ? "Operator"
                        : u.role}
                    </span>
                    {propName ? (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#002FA7]/10 text-[#002FA7] flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {propName}
                      </span>
                    ) : u.role === "super_admin" || u.role === "admin" ? (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">
                        All Properties
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="shrink-0 ml-3">
                {isResetting ? (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Input
                        type={showPw ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="rounded-none h-9 text-sm w-40 pr-8"
                        placeholder="New password"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw(!showPw)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400"
                      >
                        {showPw ? (
                          <EyeOff className="h-3.5 w-3.5" />
                        ) : (
                          <Eye className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                    <button
                      onClick={() => handleReset(u.id, u.name)}
                      disabled={busy}
                      className="signage-btn-primary h-9 px-3 text-xs"
                    >
                      {busy ? "..." : "Save"}
                    </button>
                    <button
                      onClick={() => {
                        setResetId(null);
                        setNewPassword("");
                      }}
                      className="signage-btn-ghost h-9 px-3 text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setResetId(u.id)}
                    className="signage-btn-ghost h-9 px-3 text-xs flex items-center gap-1.5"
                  >
                    <Key className="h-3.5 w-3.5" />
                    Reset Password
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
